import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import type { StoryblokRichTextNode } from '@storyblok/richtext';
import { SafeHtmlPipe } from '../../../pipes/safe-html.pipe';
import { customRenderText, fixHtml, removeHtml } from '../../custom-render-text';
import { getHeadingAnchor } from '../../get-header-anchor';
import { BlockStyle } from '../../interfaces/block-style';
import { BlockWrapperComponent } from '../block-wrapper/block-wrapper.component';

const calcCustomLineHeight = (size: number) => {
  if (!size) return;
  if (size <= 24) return 1.2;
  if (size <= 48) return 1.2;
  return 1;
};

/** Storyblok number fields: unset as undefined/null/"", and often 0 after null clears.
 * Keep treating 0 as unset until THEME-MIGRATION.md “Title custom*Size null → 0” is resolved. */
const isSizeSet = (v: number | string | null | undefined): v is number | string =>
  v != null && v !== '' && Number(v) !== 0;

const HEADING_LEVELS = [1, 2, 3, 4] as const;
type HeadingTag = `h${(typeof HEADING_LEVELS)[number]}`;

/** First block in titleText — heading level + textAlign source. */
function firstRichBlock(titleText: StoryblokRichTextNode<string> | undefined) {
  const block = titleText?.content?.[0] as
    | {
        type?: string;
        attrs?: { level?: number; textAlign?: string | null };
        content?: unknown[];
      }
    | undefined;
  return block;
}

function stripToInlineMarkup(html: string): string {
  let open = false;
  return html.replace(/<[^>]*>?/gm, (match: string) => {
    if (match === '<span style="color:#4353FF">' || match === '<span style="color:#FB4F4F">') {
      open = true;
      return match;
    }
    if (match === '</span>' && open) {
      open = false;
      return match;
    }
    // Keep highlight marks; normalize to a bare tag so the global
    // <mark> highlighter style applies (same as testimonials).
    if (/^<mark[\s>]/i.test(match)) return '<mark>';
    if (match === '</mark>') return '</mark>';
    return '';
  });
}

@Component({
  selector: 'app-title',
  templateUrl: './title.component.html',
  styleUrls: ['./title.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BlockWrapperComponent, SafeHtmlPipe],
})
export class TitleComponent {
  readonly _uid = input<string>(undefined); // just here to prevent errors
  readonly component = input<string>(undefined); // just here to prevent errors
  readonly _editable = input<string>(undefined);
  readonly style = input<BlockStyle[]>(undefined);
  readonly textSize = input<string>(undefined);
  readonly textStyle = input<string>(undefined);
  readonly titleText = input<StoryblokRichTextNode<string>>(undefined);
  readonly customLargeDesktopSize = input<number>(undefined);
  readonly customDesktopSize = input<number>(undefined);
  readonly customTabletSize = input<number>(undefined);
  readonly customMobileSize = input<number>(undefined);

  readonly mobileAlignment = input<string>(undefined);
  readonly tabletAlignment = input<string>(undefined);
  readonly desktopAlignment = input<string>(undefined);

  /** True when any custom* px field has a real value (empty/0 = unset). */
  readonly hasSizeOverride = computed(
    () =>
      isSizeSet(this.customMobileSize()) ||
      isSizeSet(this.customTabletSize()) ||
      isSizeSet(this.customDesktopSize()) ||
      isSizeSet(this.customLargeDesktopSize())
  );

  readonly effectiveSizeClass = computed(() => `size-${this.textSize() || 'display-md'}`);
  readonly effectiveStyleClass = computed(() => `style-${this.textStyle() || 'text-strong'}`);

  /** Outer heading tag from titleText first node. */
  readonly headingTag = computed((): HeadingTag => {
    const block = firstRichBlock(this.titleText());
    const level = block?.type === 'heading' ? Number(block.attrs?.level) : NaN;
    if ((HEADING_LEVELS as readonly number[]).includes(level)) return `h${level}` as HeadingTag;
    return 'h2';
  });

  readonly headingLevel = computed(() => Number(this.headingTag().slice(1)));

  /** Align from titleText first node. */
  readonly effectiveAlignment = computed(() => {
    const align = firstRichBlock(this.titleText())?.attrs?.textAlign;
    if (align === 'left' || align === 'center' || align === 'right') return align;
    return 'center';
  });

  readonly sizeOverrideStyles = computed(() => {
    if (!this.hasSizeOverride()) return null;
    const mobile = this.customMobileSize();
    const tablet = this.customTabletSize();
    const desktop = this.customDesktopSize();
    const largeDesktop = this.customLargeDesktopSize();
    const styles: Record<string, string | number> = {};
    if (isSizeSet(mobile)) {
      styles['--mobile-size'] = `${mobile}px`;
      const lh = calcCustomLineHeight(Number(mobile));
      if (lh != null) styles['--mobile-line-height'] = lh;
    }
    if (isSizeSet(tablet)) {
      styles['--tablet-size'] = `${tablet}px`;
      const lh = calcCustomLineHeight(Number(tablet));
      if (lh != null) styles['--tablet-line-height'] = lh;
    }
    if (isSizeSet(desktop)) {
      styles['--desktop-size'] = `${desktop}px`;
      const lh = calcCustomLineHeight(Number(desktop));
      if (lh != null) styles['--desktop-line-height'] = lh;
    }
    if (isSizeSet(largeDesktop)) {
      styles['--large-desktop-size'] = `${largeDesktop}px`;
      const lh = calcCustomLineHeight(Number(largeDesktop));
      if (lh != null) styles['--large-desktop-line-height'] = lh;
    }
    return Object.keys(styles).length ? styles : null;
  });

  /** Inline markup from first titleText node only (later blocks ignored). */
  readonly sanitizedTitle = computed(() => {
    const titleText = this.titleText();
    const block = firstRichBlock(titleText);
    if (!block) return;
    const doc = { type: 'doc', content: [block] } as StoryblokRichTextNode<string>;
    return stripToInlineMarkup(fixHtml(customRenderText(doc)));
  });

  readonly finalAnchor = computed(() => {
    const sanitizedTitle = this.sanitizedTitle();
    if (!sanitizedTitle) return;
    // Anchor from plain text only, so inline tags (mark, color spans) don't leak into the id.
    return getHeadingAnchor(this.headingLevel(), removeHtml(sanitizedTitle));
  });
}
